import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import ProfileHeader from './components/ProfileHeader';
import AccountInformation from './components/AccountInformation';
import DisplayPreferences from './components/DisplayPreferences';
import PersonalNotifications from './components/PersonalNotifications';
import Button from '../../components/ui/Button';
import TabNavigation from '../../components/ui/TabNavigation';
import SettingsPreview from './components/SettingsPreview';



const IndividualProfileManagement = () => {
  const [selectedMember, setSelectedMember] = useState(null);
  const [activeTab, setActiveTab] = useState('account');
  const [saveStatus, setSaveStatus] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const [familyMembers, setFamilyMembers] = useState([
    { id: 1, name: 'Sarah', role: 'Mom', color: '#EF4444', initials: 'SM' },
    { id: 2, name: 'Michael', role: 'Dad', color: '#3B82F6', initials: 'MD' },
    { id: 3, name: 'Emma', role: 'Daughter', color: '#EC4899', initials: 'ED' },
    { id: 4, name: 'Lucas', role: 'Son', color: '#10B981', initials: 'LS' },
    { id: 5, name: 'Olivia', role: 'Daughter', color: '#F59E0B', initials: 'OD' },
    { id: 6, name: 'Noah', role: 'Son', color: '#8B5CF6', initials: 'NS' }
  ]);

  const [profileSettings, setProfileSettings] = useState({});

  useEffect(() => {
    const savedSettings = localStorage?.getItem('individualProfileSettings');
    if (savedSettings) {
      try {
        setProfileSettings(JSON.parse(savedSettings));
      } catch (e) {
        console.error('Failed to load profile settings', e);
      }
    } else {
      const defaultSettings = {};
      familyMembers?.forEach(member => {
        defaultSettings[member?.id] = {
          accountInfo: {
            name: member?.name,
            role: member?.role,
            email: '',
            phone: ''
          },
          displayPreferences: {
            calendarView: 'month',
            taskListSort: 'priority',
            colorTheme: member?.color,
            fontSize: 'medium',
            highContrast: false,
            showCompletedTasks: true,
            weekStartsOn: 'sunday',
            holidayLocation: 'United States',
            showPublicHolidays: true,
            showSchoolTerms: true
          },
          notifications: {
            messages: { enabled: true, sound: true, timing: 'immediate' },
            taskReminders: { enabled: true, sound: true, timing: '15min' },
            calendarAlerts: { enabled: true, sound: true, timing: '30min' },
            familyActivity: { enabled: true, sound: false, timing: 'digest' },
            deliveryMethod: ['push', 'email'],
            priority: 'normal',
            crossDeviceSync: true,
            reminderFrequency: ['15min', '1day'],
            deliveryTiming: 'smart',
            quietHoursStart: '22:00',
            quietHoursEnd: '07:00',
            notifyForRecurring: true
          }
        };
      });
      setProfileSettings(defaultSettings);
    }

    if (!selectedMember && familyMembers?.length > 0) {
      setSelectedMember(familyMembers?.[0]);
    }
  }, []);

  const tabs = [
    { id: 'account', label: 'Account Information', icon: 'user' },
    { id: 'display', label: 'Display Preferences', icon: 'monitor' },
    { id: 'notifications', label: 'Personal Notifications', icon: 'bell' }
  ];

  const handleMemberSelect = (member) => {
    if (hasChanges) {
      const confirmSwitch = window.confirm('You have unsaved changes. Do you want to discard them?');
      if (!confirmSwitch) return;
      setHasChanges(false);
    }
    setSelectedMember(member);
    setActiveTab('account');
  };

  const handleSettingsUpdate = (section, updates) => {
    if (!selectedMember) return;
    
    setProfileSettings(prev => ({
      ...prev,
      [selectedMember?.id]: {
        ...prev?.[selectedMember?.id],
        [section]: {
          ...prev?.[selectedMember?.id]?.[section],
          ...updates
        }
      }
    }));
    setHasChanges(true);
  };

  const handleMemberColorUpdate = (newColor) => {
    if (!selectedMember) return;

    // Update familyMembers state
    setFamilyMembers(prev => prev?.map(member => 
      member?.id === selectedMember?.id 
        ? { ...member, color: newColor }
        : member
    ));

    // Update selectedMember
    setSelectedMember(prev => ({ ...prev, color: newColor }));

    // Update colorTheme in displayPreferences
    handleSettingsUpdate('displayPreferences', { colorTheme: newColor });
  };

  const handleSave = () => {
    setSaveStatus('saving');
    
    setTimeout(() => {
      try {
        localStorage?.setItem('individualProfileSettings', JSON.stringify(profileSettings));
        setSaveStatus('success');
        setHasChanges(false);
        
        setTimeout(() => {
          setSaveStatus(null);
        }, 3000);
      } catch (e) {
        setSaveStatus('error');
        setTimeout(() => {
          setSaveStatus(null);
        }, 3000);
      }
    }, 1000);
  };

  const handleCancel = () => {
    const confirmCancel = window.confirm('Are you sure you want to discard your changes?');
    if (confirmCancel) {
      window?.location?.reload();
    }
  };

  const currentSettings = selectedMember ? profileSettings?.[selectedMember?.id] : null;

  return (
    <>
      <Helmet>
        <title>My Profile - Starling Schedule</title>
      </Helmet>
      <div className="min-h-screen bg-background">
      <TabNavigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-h1 font-heading text-foreground mb-2">
                Individual Profile Management
              </h1>
              <p className="text-muted-foreground">
                Personalized account control with profile customization, display preferences, and notification settings
              </p>
            </div>
            <div className="flex gap-3">
              {hasChanges && (
                <Button
                  variant="outline"
                  onClick={handleCancel}
                >
                  <Icon name="x" size={16} className="mr-2" />
                  Cancel
                </Button>
              )}
              <Button
                onClick={handleSave}
                disabled={!hasChanges}
              >
                <Icon name="check" size={16} className="mr-2" />
                Save Changes
              </Button>
            </div>
          </div>

          {/* Save Status */}
          {saveStatus && (
            <div className={`mt-4 p-4 rounded-lg flex items-center gap-3 ${
              saveStatus === 'success' ? 'bg-success/10 text-success' : 
              saveStatus === 'error' ? 'bg-error/10 text-error' : 'bg-muted/30 text-muted-foreground'
            }`}>
              <Icon 
                name={saveStatus === 'success' ? 'check-circle' : saveStatus === 'error' ? 'x-circle' : 'loader'} 
                size={20}
                className={saveStatus === 'saving' ? 'animate-spin' : ''}
              />
              <span>
                {saveStatus === 'success' ? 'Settings saved successfully!' :
                 saveStatus === 'error' ? 'Failed to save settings. Please try again.' : 'Saving settings...'}
              </span>
            </div>
          )}
        </div>

        {/* Member Selection */}
        <div className="mb-8">
          <h2 className="text-h3 font-heading text-foreground mb-4">Select Family Member</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
            {familyMembers?.map(member => (
              <button
                key={member?.id}
                onClick={() => handleMemberSelect(member)}
                className={`p-4 rounded-lg border-2 transition-all hover:shadow-warm-md ${
                  selectedMember?.id === member?.id
                    ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-primary/50'
                }`}
              >
                <div
                  className="w-16 h-16 mx-auto mb-2 rounded-full flex items-center justify-center text-white font-semibold text-lg"
                  style={{ backgroundColor: member?.color }}
                >
                  {member?.initials}
                </div>
                <div className="text-center">
                  <div className="font-semibold text-foreground text-sm">{member?.name}</div>
                  <div className="text-xs text-muted-foreground">{member?.role}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Profile Content */}
        {selectedMember && currentSettings && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Settings Panel */}
            <div className="lg:col-span-2 space-y-6">
              {/* Profile Header */}
              <ProfileHeader member={selectedMember} />

              {/* Tabs */}
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                <div className="flex border-b border-border overflow-x-auto">
                  {tabs?.map(tab => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                        activeTab === tab?.id
                          ? 'bg-primary/5 text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                      }`}
                    >
                      <Icon name={tab?.icon} size={18} />
                      <span className="hidden sm:inline">{tab?.label}</span>
                    </button>
                  ))}
                </div>

                {/* Tab Content */}
                <div className="p-6">
                  {activeTab === 'account' && (
                    <AccountInformation
                      settings={currentSettings?.accountInfo}
                      memberColor={selectedMember?.color}
                      onChange={(updates) => handleSettingsUpdate('accountInfo', updates)}
                    />
                  )}
                  {activeTab === 'display' && (
                    <DisplayPreferences
                      settings={currentSettings?.displayPreferences}
                      memberColor={selectedMember?.color}
                      onChange={(updates) => handleSettingsUpdate('displayPreferences', updates)}
                      onColorChange={handleMemberColorUpdate}
                    />
                  )}
                  {activeTab === 'notifications' && (
                    <PersonalNotifications
                      settings={currentSettings?.notifications}
                      memberColor={selectedMember?.color}
                      onChange={(updates) => handleSettingsUpdate('notifications', updates)}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Preview Panel */}
            <div className="lg:col-span-1">
              <SettingsPreview
                member={selectedMember}
                settings={currentSettings}
                activeTab={activeTab}
              />
            </div>
          </div>
        )}
      </div>
      </div>
    </>
  );
};

export default IndividualProfileManagement;